import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Heart, ArrowRight, CheckCircle2, Gift, ChevronDown, ChevronUp, Sparkles, Check } from 'lucide-react';
import BetaInquiryDialog from '../components/landing/BetaInquiryDialog';

const CircleLogo = ({ size = 'md' }) => {
  const sizes = {
    sm: { outer: 'w-10 h-10', mid: 'inset-1', inner: 'w-4 h-4', heart: 'w-2 h-2' },
    md: { outer: 'w-14 h-14', mid: 'inset-1.5', inner: 'w-6 h-6', heart: 'w-3 h-3' },
    lg: { outer: 'w-20 h-20', mid: 'inset-2', inner: 'w-8 h-8', heart: 'w-4 h-4' },
  };
  const s = sizes[size];
  return (
    <div className={`relative ${s.outer} flex items-center justify-center`}>
      <div className="absolute inset-0 rounded-full border border-cyan-300/40" />
      <div className={`absolute ${s.mid} rounded-full border border-cyan-400/60`} />
      <div className={`${s.inner} bg-red-500 rounded-full flex items-center justify-center`}>
        <Heart className={`${s.heart} text-white`} fill="currentColor" />
      </div>
    </div>
  );
};

const FaqItem = ({ q, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/30 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-3.5 text-left" data-testid={`faq-${q.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}>
        <p className="text-sm font-semibold text-foreground pr-4">{q}</p>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {open && <div className="pb-4 -mt-1">{children}</div>}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [pilotOpen, setPilotOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">

      {/* SLIM TOP BAR — For Individuals | Logo | For Organizations */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border/30">
        <nav className="max-w-lg mx-auto px-3 h-12 grid grid-cols-[1fr_auto_1fr] items-center gap-2" aria-label="Primary">
          <span
            className="justify-self-start text-[12px] sm:text-sm font-semibold text-cyan-600 px-1.5 py-1"
            aria-current="page"
            data-testid="nav-for-individuals-current"
          >
            For Individuals
          </span>
          <div className="justify-self-center flex items-center gap-2">
            <CircleLogo size="sm" />
            <span className="hidden sm:inline text-sm font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>CircleUpSquad</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="justify-self-end text-[12px] sm:text-sm font-medium text-muted-foreground hover:text-foreground px-1.5 py-1 rounded-md transition-colors"
            data-testid="nav-for-organizations"
          >
            For Organizations
          </button>
        </nav>
      </header>

      {/* HERO — clean: logo + name + subtitle + CTA */}
      <section className="px-6 flex flex-col justify-center min-h-[calc(100dvh-3rem)] max-w-lg mx-auto">
        <div className="space-y-5 fade-in">
          <div className="flex justify-center mb-2">
            <CircleLogo size="lg" />
          </div>

          <h1
            className="text-3xl sm:text-4xl leading-[1.15] font-bold text-foreground tracking-tight text-center"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-title"
          >
            CircleUpSquad
          </h1>

          <p className="text-sm text-muted-foreground leading-relaxed text-center">
            For adults living with cancer or serious illness and the family, friends, and caregivers supporting them. Coordinate care, share updates, and make it easier for everyone to help.
          </p>

          <div className="pt-3 space-y-3">
            <Button
              onClick={() => navigate('/auth')}
              className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white shadow-lg shadow-cyan-600/20"
              data-testid="hero-cta-btn"
            >
              Create a Care Circle <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button
              onClick={() => navigate('/auth?mode=join')}
              variant="outline"
              className="w-full h-[48px] rounded-xl text-sm font-semibold"
              data-testid="join-code-btn"
            >
              I have an invite code
            </Button>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/auth')}
                className="text-[13px] text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                data-testid="signin-link"
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 py-12 border-t border-border/30" data-testid="how-it-works">
        <div className="max-w-lg mx-auto space-y-6">
          <h2 className="text-lg font-bold text-foreground text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How it works
          </h2>
          <div className="space-y-4">
            {[
              { step: '1', text: 'Create a private Care Circle' },
              { step: '2', text: 'Invite your support network' },
              { step: '3', text: 'Post real needs (rides, meals, check-ins)' },
              { step: '4', text: 'People claim what they can do' },
              { step: '5', text: "Everyone sees what's done — and what still needs support" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-cyan-700 dark:text-cyan-300">{item.step}</span>
                </div>
                <p className="text-sm text-foreground pt-1.5">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CARE PROFILE QUIZ — no login required */}
      <section className="px-6 py-12 border-t border-border/30" data-testid="care-quiz-cta">
        <div className="max-w-lg mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-50 via-emerald-50 to-amber-50 dark:from-cyan-950/30 dark:via-emerald-950/20 dark:to-amber-950/20 p-6 border border-cyan-100 dark:border-cyan-900/40">
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-cyan-200/30 blur-2xl" aria-hidden="true" />
            <div className="relative space-y-4">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 dark:bg-background/60 border border-cyan-200/60 text-[11px] font-semibold text-cyan-700 dark:text-cyan-300">
                <Sparkles className="w-3 h-3" /> No login needed
              </div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Vibe Check: What{"'"}s your Care Profile Style?
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Answer a few quick questions to find out how you prefer to give and receive support.
              </p>
              <ul className="space-y-1.5 text-sm text-foreground/90">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> 8–10 minutes · 8 short sections</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Private — your answers are only shared if you choose to</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Emailed to you when you finish</li>
              </ul>
              <Button
                className="btn-primary w-full"
                onClick={() => navigate('/care-profile-quiz')}
                data-testid="landing-start-quiz-btn"
              >
                Start the Quick Questions <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                No sign-up until you decide you want one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTEXT BOXES — moved below How It Works */}
      <section className="px-6 py-10 border-t border-border/30">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 space-y-3">
            <p className="text-sm font-semibold text-foreground leading-snug">
              When a working adult is diagnosed with cancer, life doesn't pause — but everything becomes harder to manage.
            </p>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-muted-foreground/50 mt-0.5">-</span> people want to help, but don't know what actually helps</li>
              <li className="flex items-start gap-2"><span className="text-muted-foreground/50 mt-0.5">-</span> support gets scattered across texts and conversations</li>
              <li className="flex items-start gap-2"><span className="text-muted-foreground/50 mt-0.5">-</span> one person often carries the coordination load</li>
              <li className="flex items-start gap-2"><span className="text-muted-foreground/50 mt-0.5">-</span> good intentions don't always turn into follow-through</li>
            </ul>
          </div>

          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-5 space-y-3">
            <p className="text-sm font-semibold text-foreground leading-snug">
              CircleUpSquad turns support into clear, shared action.
            </p>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> requests for help are visible in one place</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> people claim what they can realistically do</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> everyone sees what's handled and what still needs support</li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT THIS IS */}
      <section className="px-6 py-10 border-t border-border/30">
        <div className="max-w-lg mx-auto space-y-4">
          <h2 className="text-lg font-bold text-foreground text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What this is (and isn't)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-4">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">This is:</p>
              <p className="text-sm text-muted-foreground">a coordination tool for real-life cancer support during everyday life</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4">
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">This is not:</p>
              <p className="text-sm text-muted-foreground">a group chat replacement, social network, or medical records system</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOR ORGANIZATIONS CALLOUT */}
      <section className="px-6 py-12 border-t border-border/30" data-testid="for-organizations-callout">
        <div className="max-w-lg mx-auto">
          <div className="rounded-2xl border border-cyan-200/80 dark:border-cyan-900/50 bg-cyan-50/60 dark:bg-cyan-950/20 p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-400">For Organizations</p>
            <h2 className="text-lg md:text-lg font-bold text-foreground mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Care as a Collective Practice
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              We help organizations strengthen how care is communicated, coordinated, and delivered through workforce training, coaching and technical assistance, and care coordination technology.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="mt-5 w-full h-[48px] rounded-xl text-sm font-semibold bg-cyan-700 hover:bg-cyan-800 text-white"
              data-testid="callout-partner-btn"
            >
              Explore Organizational Partnerships <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-10 border-t border-border/30" data-testid="faq-section">
        <div className="max-w-lg mx-auto space-y-4">
          <h2 className="text-lg font-bold text-foreground text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Quick questions
          </h2>
          <div>
            <FaqItem q="Is this replacing group texts?">
              <p className="text-sm text-muted-foreground">No. It reduces confusion by organizing what people already intend to do.</p>
            </FaqItem>
            <FaqItem q="Who is this for?">
              <p className="text-sm text-muted-foreground">Working adults living with cancer/serious illness — and the care partners (friends and family) who will support them through treatment.</p>
            </FaqItem>
            <FaqItem q="Is it private?">
              <p className="text-sm text-muted-foreground">Yes. Every Care Circle is invite-only.</p>
            </FaqItem>
            <FaqItem q="Is it hard to use?">
              <p className="text-sm text-muted-foreground">No. It's built around simple actions: request help, claim help, complete help.</p>
            </FaqItem>
            <FaqItem q="How much does it cost?">
              <p className="text-sm text-muted-foreground">
                It{"'"}s free during our beta.
              </p>
            </FaqItem>
            <FaqItem q="Can I give this to someone?">
              <p className="text-sm text-muted-foreground">
                Yes! You can gift a CircleUpSquad subscription to someone you care about.{' '}
                <button onClick={() => navigate('/gift-intro')} className="text-cyan-600 hover:text-cyan-700 font-medium underline underline-offset-2">
                  Click here to send a gift subscription.
                </button>
              </p>
            </FaqItem>
            <FaqItem q="What's next for CircleUpSquad?">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  We{"'"}re demoing with partner orgs and setting up pilots.
                </p>
                <Button size="sm" onClick={() => setPilotOpen(true)} className="btn-primary" data-testid="faq-pilot-btn">
                  Discuss a pilot
                </Button>
              </div>
            </FaqItem>
            <FaqItem q="About the founder">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <img src="/images/shannon-portrait.jpg" alt="Shannon Ivey" className="w-11 h-11 rounded-full object-cover bg-white flex-shrink-0" />
                  <p className="text-sm font-semibold text-foreground">Hey, I{"'"}m Shannon.</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When I was diagnosed with late stage cancer at 42, I was a solo parent living 9 hours from my family. I saw how coordinating the support during treatment while working/parenting became overwhelming. Over the last 10 years, as a cancer patient advocate, I realized that not only was I not alone, but also it was a common experience of people parenting during cancer treatment.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  CircleUpSquad helps you and the people who care about you understand what support is needed — and makes it easier to help without confusion or overwhelm.
                </p>
                <p className="text-sm font-medium text-foreground">You don{"'"}t have to do this alone.</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  You just need your helpers in one place (not a Facebook group! or gasp — group text). That{"'"}s CircleUpSquad{"'"}s mission.
                </p>
                <div className="pt-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-cyan-600 mb-2">Watch Shannon{"'"}s TEDx</p>
                  <div className="rounded-xl overflow-hidden shadow border border-border/50" style={{ aspectRatio: '16 / 9' }}>
                    <iframe
                      src="https://www.youtube.com/embed/SloEhSgZOQ4"
                      title="Shannon Ivey TEDx — Ring Theory and care coordination"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid="tedx-video"
                    />
                  </div>
                </div>
              </div>
            </FaqItem>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-12 bg-foreground dark:bg-slate-800" data-testid="final-cta-section">
        <div className="max-w-lg mx-auto space-y-5 text-center">
          <div className="flex justify-center">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border border-white/20" />
              <div className="absolute inset-1.5 rounded-full border border-white/30" />
              <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                <Heart className="w-2.5 h-2.5 text-white" fill="currentColor" />
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start coordinating support in minutes
          </h2>

          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white"
            data-testid="final-cta-btn"
          >
            Create a Care Circle
          </Button>

          <button
            onClick={() => navigate('/gift-intro')}
            className="inline-flex items-center gap-1.5 text-[13px] text-white/50 hover:text-white/80 transition-colors pt-1"
            data-testid="gift-link"
          >
            <Gift className="w-3.5 h-3.5" /> Gift CircleUpSquad to someone
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 py-10 text-center">
        <div className="max-w-lg mx-auto space-y-3">
          <div className="flex justify-center">
            <CircleLogo size="sm" />
          </div>
          <p className="text-sm font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CircleUpSquad<sup className="text-[7px] text-muted-foreground align-super ml-0.5">TM</sup>
          </p>
          <p className="text-[12px] text-muted-foreground/50 italic">
            Private, patient-directed care coordination
          </p>
        </div>
      </footer>

      <BetaInquiryDialog open={pilotOpen} onOpenChange={setPilotOpen} kind="pilot" />
    </div>
  );
};

export default LandingPage;
